import { useTranslation } from "react-i18next";
import { useCallback, useState } from "react";
import toast from "react-hot-toast";

import { useSwitchCompanyMutation } from "@/services/(my-space)/userApi";
import {
  getAuthTokensFromLocalStorage,
  setAuthTokensToCookie,
} from "@/utils/auth";

/** Delay before the reload so the success toast is legible first. */
const RELOAD_DELAY_MS = 300;

export interface CompanySwitchState {
  /** Id of the company currently being switched to, or `null` when idle. */
  switchingCompanyId: number | null;
  /** True while any switch is in flight — use it to lock every row. */
  isSwitching: boolean;
  switchTo: (companyId: number) => Promise<void>;
}

/**
 * Owns the "switch active company" flow for the whole companies list.
 *
 * Deliberately hoisted above the individual rows: one mutation instance for
 * the list means a switch already in flight locks every other row, instead of
 * each row owning its own mutation and allowing two concurrent switches.
 *
 * Ends in a full page reload — the active company is baked into the auth
 * token, so every RTK Query cache, permission set and route guard has to be
 * re-initialised from scratch.
 */
export const useCompanySwitch = (): CompanySwitchState => {
  const { t } = useTranslation(["common", "company"]);
  const [switchCompany] = useSwitchCompanyMutation();
  const [switchingCompanyId, setSwitchingCompanyId] = useState<number | null>(
    null,
  );

  const switchTo = useCallback(
    async (companyId: number) => {
      // Guard against a second click landing while the first is in flight.
      if (switchingCompanyId !== null) return;
      setSwitchingCompanyId(companyId);

      const toastId = toast.loading(t("company:shared.processing"));
      try {
        const res = await switchCompany({ companyId }).unwrap();
        const { accessToken, refreshToken, deviceId } = res.data;

        // Preserve the user's existing rememberMe so the cookie lifetime
        // doesn't silently downgrade to 1 day on company switch.
        const rememberMe = getAuthTokensFromLocalStorage()?.rememberMe;
        if (accessToken && deviceId)
          setAuthTokensToCookie(
            accessToken,
            refreshToken,
            deviceId,
            rememberMe,
          );

        toast.success(res.message, { id: toastId });
        toast.loading(t("common:chrome.switching_your_company"), {
          id: toastId,
        });
        // Intentionally leaves `switchingCompanyId` set — the row stays locked
        // until the reload tears the tree down.
        setTimeout(() => window.location.reload(), RELOAD_DELAY_MS);
      } catch (error: any) {
        setSwitchingCompanyId(null);
        toast.error(
          error?.data?.message ??
            error?.response?.data?.message ??
            error?.message ??
            t("my-space:toasts.something_went_wrong_please_try_again_later"),
          { id: toastId },
        );
      }
    },
    [switchCompany, switchingCompanyId, t],
  );

  return {
    switchingCompanyId,
    isSwitching: switchingCompanyId !== null,
    switchTo,
  };
};
